import { Injectable } from '@angular/core';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { MeSuccessDialogService } from '../../components/me-dialog/me-success/me-success.service';
import { UserService } from '../user/user.service';
import { ExternalSettings } from '../external-settings/external-settings.interface';
import { AccountsService } from '../accounts/accounts.service';
import { InternalSettingsService } from '../internal-settings/internal-settings.service';
import { ExternalSettingsService } from '../external-settings/external-settings.service';
import { AsyncSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { SessionConfirmService } from './session-confirm/session-confirm.service';
import { ConfirmDialogConfig } from './session-confirm/session-confirm.component';
import { NavigationEnd, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { logger } from '../logging/logging.service';
import { PlatformService } from 'src/app/system/utility/platform.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  readonly debounceSubject = new Subject<DebounceType>();

  // Unit is seconds
  private static readonly DIALOG_COUNTDOWN_TIME = 30;   // Dialog popup with this time remaining
  private static readonly BUFFER_INTERVAL = 10;         // Buffer interval to show dialog (Before real logoff)
  private static readonly INTERVAL = 1;                 // Checking Time interval
  private static readonly POPUP_TIME = SessionService.DIALOG_COUNTDOWN_TIME + SessionService.BUFFER_INTERVAL; // Actual Popup time
  private static readonly DEBUG_TIME = 120;             // Debug time

  private remainingTime!: DateTime;
  private serverHandshakeTime!: DateTime;
  private externalSettings!: ExternalSettings;
  private timer!: any;

  constructor(
    private srvConfirmDialog: SessionConfirmService,
    private srvSucessDialog: MeSuccessDialogService,
    private translate: TranslateUtilityService,
    private srvUser: UserService,
    private srvAccount: AccountsService,
    private srvES: ExternalSettingsService,
    private srvIS: InternalSettingsService,
    private router: Router,
  ) {

    this.registerPageChangeEvent();
    this.registerKeyboardEvent();

    combineLatest([
      this.srvES.externalSettings$,
    ]).subscribe(([externalSettings]) => {

      if (externalSettings == null) return;
      this.externalSettings = externalSettings;

      this.srvUser.isLoggedIn$.subscribe((isLoggedIn: boolean) => {
        (isLoggedIn) ? this.initTimer() : this.housekeep();
      });
    });
  }

  public init() {

    // Debounce the event to save resources
    this.debounceSubject.pipe(
      debounceTime(1000),
    ).subscribe((type: DebounceType) => {
      if (this.isAcceptingExtension()) {
        switch (type) {
          case DebounceType.KEYBOARD:
            this.extendClientSession();
            break;
          case DebounceType.PAGE_CHANGE:
            this.extendClientSession();
            this.extendServerSession();
            break;
        }
      };
    });

    let ret = new AsyncSubject<boolean>();
    ret.next(true);
    ret.complete();
    return ret;
  }

  // Listen to Page Change Event. Assume page change will have API call to server
  private registerPageChangeEvent() {
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      distinctUntilChanged(),
    ).subscribe((temp: any) => {
      this.debounceSubject.next(DebounceType.PAGE_CHANGE);
      this.extendServerSession();
    });
  }

  private registerKeyboardEvent() {
    if (PlatformService.isMobile()) {
      document.addEventListener('input', () => {
        this.debounceSubject.next(DebounceType.KEYBOARD);
      });
    } else {
      document.addEventListener('keyup', () => {
        this.debounceSubject.next(DebounceType.KEYBOARD);
      });
    }
  }

  private initTimer() {

    if (this.timer != null) return;

    this.extendClientSession();
    this.extendServerSession();

    this.timer = setInterval(() => {

      let now = DateTime.now();

      const remainTime = Math.round(this.remainingTime.diff(now, 'seconds').seconds);

      if (remainTime <= SessionService.DEBUG_TIME) {
        logger.trace(`Remaining Time : Now : ${now.toISOTime()}. Client" + ${this.remainingTime.toISOTime()}. Server : ${this.serverHandshakeTime.toISOTime()} --> Count Down ${remainTime - (SessionService.BUFFER_INTERVAL)})`);
      }

      // This is to prevent the browser is being minimized and then restore after the session is expired
      if (remainTime <= SessionService.BUFFER_INTERVAL) {
        this.srvUser.logoff();
      }

      if (remainTime <= SessionService.POPUP_TIME) {
        this.showConfirmDialog();
      }

      const serverRemainTime = this.serverHandshakeTime.diff(now, 'seconds').seconds;
      if (serverRemainTime <= 0) {
        this.prolongServerSession(true);
      }
    }, SessionService.INTERVAL * 1000);
  }

  private stopTimer() {
    clearInterval(this.timer);
    this.timer = null;
  }

  public showConfirmDialog() {

    if (this.srvConfirmDialog.isDialogOpen()) return;

    let config: ConfirmDialogConfig = {
      service: this,
      externalSettings: this.externalSettings,
      remainingTime: SessionService.DIALOG_COUNTDOWN_TIME,
    }

    this.srvConfirmDialog.showDialog(config).subscribe((result) => {
      if (result) {
        this.extendClientSession();
        this.prolongServerSession(false); // Extend Server Session
      } else {
        this.srvUser.logoff();
      }
    });
  }

  public extendClientSession(): void {
    if (this.externalSettings == null) return;
    logger.trace(`Extending Client Session`);
    let newTimeout = this.externalSettings.systemVariables.customerWebTimeout;  // Client Session Timeout
    this.remainingTime = DateTime.now().plus({ seconds: newTimeout });
  }

  private extendServerSession(): void {

    if (this.externalSettings == null) return;

    logger.trace(`Extending Server Session`);
    let newTimeout = (this.externalSettings.systemVariables.customerWebTimeout / 2); // Half of Customer Session Timeout
    this.serverHandshakeTime = DateTime.now().plus({ seconds: newTimeout });
  }

  private prolongServerSession(isSilent: boolean) {
    this.extendServerSession();
    this.srvUser.doPing().subscribe((result) => {
      if (result && !isSilent) {
        let title = this.translate.instant('SESSION_DIALOG.SESSION_CONFIRM_TITLE');
        let message = this.translate.instant('SESSION_DIALOG.SESSION_IS_SUCCESSFULLY_PROLONGED');
        let buttonText = this.translate.instant('SESSION_DIALOG.BUTTON_OK');
        this.srvSucessDialog.show(title, message, buttonText);
      }
    });
  }

  public isAcceptingExtension(): boolean {
    if (this.timer == null) return false;
    if (this.srvConfirmDialog.isDialogOpen()) return false;
    return true;
  }

  private housekeep() {
    logger.debug(`Performing housekeeping`);
    this.srvAccount.clearAccount();
    this.srvIS.internalSettings$.next(null);
    this.stopTimer();
  }
}

export enum DebounceType {
  KEYBOARD = "KEYBOARD",
  PAGE_CHANGE = "PAGE_CHANGE",
}